import type {
	StepI,
	TriggerChoice,
	WizardFormConfig,
} from "@CommonLib/src/frontend/WizardForm/types";
import {
	findChildren,
	generateIdentifier,
	getRootStep,
	getSiblings,
	getStepFromId,
	stepsUsingStepAsCondition,
} from "@CommonLib/src/frontend/WizardForm/utils";
import { Add, Delete, Edit } from "@mui/icons-material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
	Box,
	Button,
	CardContent,
	Chip,
	Collapse,
	IconButton,
	List,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Select,
	TextField,
	Tooltip,
} from "@mui/material";
import * as React from "react";

function buildTriggerOptions(
	config: WizardFormConfig,
	parentStepId: number | null,
	stepId: number | null,
) {
	const options: {
		key: string;
		stepId: number;
		fieldId: number;
		choiceId: number;
		label: string;
		disabled: boolean;
	}[] = [];

	if (parentStepId === null) return options;

	const siblings = getSiblings(config, parentStepId, stepId);

	const firstSiblingTriggerChoice = siblings[0]?.triggerChoice;

	const parentStep = getStepFromId(config, parentStepId);

	for (const field of parentStep.fields) {
		if (!field.choices) continue;
		const wrongField = firstSiblingTriggerChoice
			? firstSiblingTriggerChoice.fieldId !== field.id
			: false;
		for (const choice of field.choices) {
			const siblingsDependingOnChoice = siblings.filter(
				(s) =>
					s.triggerChoice?.fieldId === field.id &&
					s.triggerChoice?.choiceId === choice.id,
			);
			const oneSiblingUsingThisChoice = siblingsDependingOnChoice[0];
			const disableMsg = wrongField
				? "All sibling steps must depend on same field"
				: oneSiblingUsingThisChoice
					? `Step ${oneSiblingUsingThisChoice.name} already depends on this choice.`
					: null;

			options.push({
				key: `${parentStep.id}-${field.id}-${choice.id}`,
				stepId: parentStep.id,
				fieldId: field.id,
				choiceId: choice.id,
				label: `${parentStep.name} → ${field.label} = "${choice.name}" ${disableMsg ? `(${disableMsg})` : ""}`,
				disabled: disableMsg !== null,
			});
		}
	}
	return options;
}

/** Build a quick index to resolve triggerChoiceId -> { fieldIdentifier, choiceName } */
const buildChoiceIndex = (config: WizardFormConfig) => {
	const idx = new Map<
		string,
		{ fieldIdentifier: string; choiceName: string }
	>();
	for (const step of config.steps) {
		for (const f of step.fields) {
			if (!f.choices) continue;
			for (const c of f.choices) {
				idx.set(generateIdentifier(step.id, f.id, c.id), {
					fieldIdentifier: f.identifier.toString(),
					choiceName: c.name,
				});
			}
		}
	}
	return idx;
};

interface StepDeleteButtonProps {
	config: WizardFormConfig;
	stepId: number;
	onDeleteStep: (stepId: number) => void;
}

const StepDeleteButton = (props: StepDeleteButtonProps) => {
	const { config, stepId, onDeleteStep } = props;
	return (
		<Tooltip
			title={
				stepsUsingStepAsCondition(config, stepId).length > 0
					? `Can't delete this step because it is used as trigger condition by: ${stepsUsingStepAsCondition(
							config,
							stepId,
						)
							.map((s) => s.name)
							.join(", ")}`
					: ""
			}
			data-testid={`step_delete_${stepId}_tooltip`}
		>
			<span>
				<IconButton
					edge="end"
					size="small"
					onClick={(e) => {
						e.stopPropagation();
						onDeleteStep(stepId);
					}}
					disabled={stepsUsingStepAsCondition(config, stepId).length > 0}
					data-testid={`step_delete_${stepId}`}
				>
					<Delete fontSize="small" />
				</IconButton>
			</span>
		</Tooltip>
	);
};

interface StepAddChildButtonProps {
	stepId: number;
	onAddStep: (stepId: number, triggerChoice?: TriggerChoice) => void;
}

const StepAddChildButton = (props: StepAddChildButtonProps) => {
	const { onAddStep, stepId } = props;
	return (
		<Button
			size="small"
			startIcon={<Add />}
			onClick={() => onAddStep(stepId)}
			data-testid={`step_add_after_${stepId}`}
		>
			Add child step
		</Button>
	);
};

interface StepAddChildWithSiblingsButtonProps {
	config: WizardFormConfig;
	stepId: number;
	fieldId: number;
	onAddStep: (stepId: number, triggerChoice?: TriggerChoice) => void;
}

const StepAddChildWithSiblingsButton = (
	props: StepAddChildWithSiblingsButtonProps,
) => {
	const { onAddStep, stepId, fieldId, config } = props;
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);

	const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleCloseMenu = () => {
		setAnchorEl(null);
	};

	const handleSelectType = (choiceId: number) => {
		onAddStep(stepId, { stepId, fieldId, choiceId });
		setAnchorEl(null);
	};

	const options = buildTriggerOptions(config, stepId, null);

	return (
		<>
			<Button
				size="small"
				startIcon={<Add />}
				onClick={handleOpenMenu}
				data-testid={`step_add_after_${stepId}`}
			>
				Add child step with siblings
			</Button>
			<Menu anchorEl={anchorEl} open={open} onClose={handleCloseMenu}>
				{options.map((choice) => {
					return (
						<MenuItem
							key={choice.key}
							onClick={() => handleSelectType(choice.choiceId)}
							disabled={choice.disabled}
						>
							{choice.label}
						</MenuItem>
					);
				})}
			</Menu>
		</>
	);
};

interface StepAddButtonProps {
	config: WizardFormConfig;
	stepId: number;
	onAddStep: (stepId: number, triggerChoice?: TriggerChoice) => void;
}

const StepAddButton = (props: StepAddButtonProps) => {
	const { config, onAddStep, stepId } = props;
	const stepChildrenWithCondition = stepsUsingStepAsCondition(config, stepId);

	const firstChildWithCondition = stepChildrenWithCondition[0];
	if (firstChildWithCondition) {
		const triggerChoice = firstChildWithCondition.triggerChoice;
		if (!triggerChoice) throw Error();
		// Normally all children using stepId for condition must point to same field (enforced by linter)
		// so we just look at first child
		const fieldId = triggerChoice.fieldId;
		// All siblings must point to same field in parent step
		const errors = stepChildrenWithCondition.filter(
			(s) =>
				s.triggerChoice?.stepId !== stepId ||
				s.triggerChoice?.fieldId !== fieldId,
		);
		if (errors.length) throw Error();
		return (
			<StepAddChildWithSiblingsButton
				onAddStep={onAddStep}
				stepId={stepId}
				fieldId={fieldId}
				config={config}
			/>
		);
	}
	return <StepAddChildButton onAddStep={onAddStep} stepId={stepId} />;
};

interface FirstStepAddButtonProps {
	config: WizardFormConfig;
	onAddStep: (stepId: number | null, triggerChoice?: TriggerChoice) => void;
}

const FirstStepAddButton = (props: FirstStepAddButtonProps) => {
	const { onAddStep, config } = props;
	if (config.steps.length > 0) return null;
	return (
		<Button size="small" startIcon={<Add />} onClick={() => onAddStep(null)}>
			Add first step
		</Button>
	);
};

interface StepEditFormProps {
	config: WizardFormConfig;
	step: StepI;
	onEditStep: (stepId: number, updates: Partial<StepI>) => void;
	setEditing: React.Dispatch<React.SetStateAction<boolean>>;
}

const StepEditForm = (props: StepEditFormProps) => {
	const { config, step, onEditStep, setEditing } = props;

	const [name, setName] = React.useState(step.name);
	const [triggerChoice, setTriggerChoice] = React.useState<TriggerChoice>(
		step.triggerChoice,
	);
	const triggerOptions = React.useMemo(
		() => buildTriggerOptions(config, step.parentStepId, step.id),
		[config, step],
	);

	const parentStepId = step.parentStepId;
	const siblings = parentStepId
		? getSiblings(config, parentStepId, step.id)
		: [];
	const hasSiblings = siblings.length > 0;

	const handleSave = () => {
		setEditing(false);
		if (name !== step.name || triggerChoice !== step.triggerChoice) {
			onEditStep(step.id, { name, triggerChoice });
		}
	};

	return (
		<Box display="flex" flexDirection="column" gap={1} sx={{ minWidth: 250 }}>
			<TextField
				size="small"
				label="Step name"
				value={name}
				onChange={(e) => setName(e.target.value)}
				name="step_name"
				inputProps={{ "data-testid": `step_edit_name_${step.id}` }}
			/>
			<Select
				size="small"
				value={
					triggerChoice
						? `${triggerChoice.stepId}:${triggerChoice.fieldId}:${triggerChoice.choiceId}`
						: ""
				}
				onChange={(e) => {
					const val = e.target.value as string;
					if (val === "") {
						setTriggerChoice(null);
					} else {
						const [sId, fId, cId] = val.split(":").map(Number) as [
							number,
							number,
							number,
						];
						setTriggerChoice({
							stepId: sId,
							fieldId: fId,
							choiceId: cId,
						});
					}
				}}
				displayEmpty
			>
				{!hasSiblings && <MenuItem value="">No trigger</MenuItem>}
				{triggerOptions.map((opt) => (
					<MenuItem
						key={opt.key}
						value={`${opt.stepId}:${opt.fieldId}:${opt.choiceId}`}
						disabled={opt.disabled}
					>
						{opt.label}
					</MenuItem>
				))}
			</Select>
			<Box display="flex" gap={1}>
				<Button
					size="small"
					variant="contained"
					onClick={(e) => {
						e.stopPropagation();
						handleSave();
					}}
				>
					Save
				</Button>
				<Button
					size="small"
					onClick={(e) => {
						e.stopPropagation();
						setEditing(false);
						setName(step.name);
						setTriggerChoice(step.triggerChoice);
					}}
				>
					Cancel
				</Button>
			</Box>
		</Box>
	);
};

interface ShowStepDetailsProps {
	config: WizardFormConfig;
	step: StepI;
}

const ShowStepDetails = (props: ShowStepDetailsProps) => {
	const { step, config } = props;
	const children = findChildren(config.steps, step.id);
	const hasChildren = children.length > 0;

	const choiceIndex = React.useMemo(() => buildChoiceIndex(config), [config]);

	return (
		<Box display="flex" alignItems="center" flexDirection="row" gap={1}>
			<Tooltip
				title={
					step.triggerChoice
						? `when "${
								choiceIndex.get(
									`${step.triggerChoice.stepId}_${step.triggerChoice.fieldId}_${step.triggerChoice.choiceId}`,
								)?.choiceName ?? "???"
							}"`
						: ""
				}
				data-testid={`step_condition_${step.id}`}
			>
				<span>{step.name}</span>
			</Tooltip>
			{hasChildren && (
				<Chip
					size="small"
					variant="outlined"
					label={`${children.length} child${children.length > 1 ? "ren" : ""}`}
					data-testid={`step_nbchildren_${step.id}`}
				/>
			)}
		</Box>
	);
};

interface StepNodeContentProps {
	config: WizardFormConfig;
	step: StepI;
	selectedStepId: number | null;
	onSelect: (
		e: React.MouseEvent<HTMLDivElement, MouseEvent>,
		id: number,
	) => void;
	depth: number;
	onDeleteStep: (stepId: number) => void;
	onEditStep: (stepId: number, updates: Partial<StepI>) => void;
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const StepNodeContent = (props: StepNodeContentProps) => {
	const {
		config,
		selectedStepId,
		onSelect,
		depth,
		step,
		open,
		setOpen,
		onDeleteStep,
		onEditStep,
	} = props;

	const [editing, setEditing] = React.useState(false);

	const children = findChildren(config.steps, step.id);
	const hasChildren = children.length > 0;

	return (
		<ListItemButton
			selected={selectedStepId === step.id}
			onClick={(e) => onSelect(e, step.id)}
			sx={{ borderRadius: 1.5, mb: 0.25, pl: 1.5 + depth * 2 }}
		>
			<ListItemIcon
				sx={{
					minWidth: 32,
					mr: 1,
					cursor: hasChildren ? "pointer" : "default",
				}}
				onClick={(e) => {
					if (!hasChildren) return;
					e.stopPropagation();
					setOpen((v) => !v);
				}}
			>
				{hasChildren ? (
					open ? (
						<ExpandMoreIcon data-testid={`step_${step.id}`} />
					) : (
						<ChevronRightIcon data-testid={`step_${step.id}`} />
					)
				) : (
					<Box sx={{ width: 24 }} />
				)}
			</ListItemIcon>

			<ListItemText
				primary={
					editing ? (
						<StepEditForm
							config={config}
							step={step}
							onEditStep={onEditStep}
							setEditing={setEditing}
						/>
					) : (
						<ShowStepDetails config={config} step={step} />
					)
				}
			/>

			{/* Action buttons */}
			{!editing && (
				<>
					<IconButton
						edge="end"
						size="small"
						onClick={(e) => {
							e.stopPropagation();
							setEditing(true);
						}}
						data-testid={`step_edit_${step.id}`}
					>
						<Edit fontSize="small" />
					</IconButton>
					<StepDeleteButton
						config={config}
						stepId={step.id}
						onDeleteStep={onDeleteStep}
					/>
				</>
			)}
		</ListItemButton>
	);
};

/** A single tree node (step) with collapsible children */
const StepNode: React.FC<{
	config: WizardFormConfig;
	step: StepI;
	selectedStepId: number | null;
	onSelect: (
		e: React.MouseEvent<HTMLDivElement, MouseEvent>,
		id: number,
	) => void;
	depth?: number;
	onAddStep: (
		parentStepId: number | null,
		triggerChoice?: TriggerChoice,
	) => void;
	onDeleteStep: (stepId: number) => void;
	onEditStep: (stepId: number, updates: Partial<StepI>) => void;
}> = ({
	config,
	step,
	selectedStepId,
	onSelect,
	depth = 0,
	onAddStep,
	onDeleteStep,
	onEditStep,
}) => {
	const children = findChildren(config.steps, step.id);
	const [open, setOpen] = React.useState<boolean>(true); // expand by default; tweak as you like

	const hasChildren = children.length > 0;

	return (
		<>
			<StepNodeContent
				config={config}
				selectedStepId={selectedStepId}
				step={step}
				setOpen={setOpen}
				open={open}
				onSelect={onSelect}
				depth={depth}
				onDeleteStep={onDeleteStep}
				onEditStep={onEditStep}
			/>

			<Box sx={{ pl: 5 + depth * 2, mb: 0.5 }}>
				<StepAddButton config={config} stepId={step.id} onAddStep={onAddStep} />
			</Box>

			{hasChildren && (
				<Collapse in={open} timeout="auto" unmountOnExit>
					<List disablePadding>
						{children.map((child) => (
							<StepNode
								key={child.id}
								config={config}
								step={child}
								selectedStepId={selectedStepId}
								onSelect={onSelect}
								depth={depth + 1}
								onAddStep={onAddStep}
								onDeleteStep={onDeleteStep}
								onEditStep={onEditStep}
							/>
						))}
					</List>
				</Collapse>
			)}
		</>
	);
};

interface StepsListProps {
	config: WizardFormConfig;
	selectedStepId: number | null;
	onAddStep: (
		parentStepId: number | null,
		triggerChoice?: TriggerChoice,
	) => void;
	onDeleteStep: (stepId: number) => void;
	handleListItemClick: (
		_event: React.MouseEvent<HTMLDivElement, MouseEvent>,
		stepId: number,
	) => void;
	onEditStep: (stepId: number, updates: Partial<StepI>) => void;
}

export const StepsList: React.FC<StepsListProps> = ({
	config,
	selectedStepId,
	handleListItemClick,
	onAddStep,
	onDeleteStep,
	onEditStep,
}) => {
	const root = React.useMemo(() => getRootStep(config.steps), [config]);

	if (!root)
		return <FirstStepAddButton config={config} onAddStep={onAddStep} />;

	return (
		<CardContent sx={{ pt: 1.5 }}>
			<List disablePadding>
				<StepNode
					key={-1}
					config={config}
					step={root}
					selectedStepId={selectedStepId}
					onSelect={handleListItemClick}
					depth={0}
					onAddStep={onAddStep}
					onDeleteStep={onDeleteStep}
					onEditStep={onEditStep}
				/>
			</List>
		</CardContent>
	);
};
